import { IStoryRepository } from './IStoryRepository';
import {
    InitWithAmplify,
    InitEnvironmentVariable,
    StoryService as PikadoStoryService,
    ChapterService as PikadoChapterService,
} from '@PikaDoApp/npm-pikado-logic';
import {
    CreateStoryInput,
    UpdateStoryInput,
    Story,
    GetStoriesResponse,
    PaginationInput,
    StoryFiltersInput,
} from '@PikaDoApp/npm-pikado-logic/dist/modules/story/graphql/story.interface';
import { AddChapterToStoryInput, Chapter, UpdateChapterInput } from '@PikaDoApp/npm-pikado-logic/dist/modules/storyChapter/graphql/storyChapter.interface';
import { Amplify } from 'aws-amplify';
import { getEnvironment } from '../../lib/getEnvironment';

// Lazy one-time initialization — runs only when first method is called,
// guaranteeing Amplify.configure() has already been called in index.tsx.
let _initialized = false;
let pikadoStoryService: PikadoStoryService;
let pikadoChapterService: PikadoChapterService;

function ensureInit() {
    if (_initialized) return;
    _initialized = true;
    InitWithAmplify(Amplify);
    InitEnvironmentVariable(getEnvironment());
    pikadoStoryService = new PikadoStoryService();
    pikadoChapterService = new PikadoChapterService();
}

export class StoryRepository implements IStoryRepository {
    async createStory(input: CreateStoryInput): Promise<Story> {
        ensureInit();
        try {
            console.log('🔍 [STORY] Creating story with input:', input);
            const result = await pikadoStoryService.createStory(input);
            console.log('✅ [STORY] Story created successfully:', result);
            return result;
        } catch (error) {
            console.error('❌ [STORY] Error creating story:', error);
            throw error;
        }
    }

    async updateStory(input: UpdateStoryInput): Promise<Story> {
        ensureInit();
        try {
            console.log('🔍 [STORY] Updating story with input:', input);
            const result = await pikadoStoryService.updateStory(input);
            console.log('✅ [STORY] Story updated successfully:', result);
            return result;
        } catch (error) {
            console.error('❌ [STORY] Error updating story:', error);
            throw error;
        }
    }

    async deleteStory(id: string): Promise<boolean> {
        ensureInit();
        try {
            console.log('🔍 [STORY] Deleting story with id:', id);
            const result = await pikadoStoryService.deleteStory(id);
            console.log('✅ [STORY] Story deleted successfully:', result);
            return result;
        } catch (error) {
            console.error('❌ [STORY] Error deleting story:', error);
            throw error;
        }
    }

    async getStoryById(id: string): Promise<Story | null> {
        ensureInit();
        try {
            console.log('🔍 [STORY] Fetching story by id:', id);
            const result = await pikadoStoryService.getStoryById(id);
            console.log('✅ [STORY] Story fetched successfully:', result);
            return result;
        } catch (error) {
            console.error('❌ [STORY] Error fetching story:', error);
            throw error;
        }
    }

    async getStories(
        pagination?: PaginationInput,
        filters?: StoryFiltersInput,
    ): Promise<GetStoriesResponse | null> {
        ensureInit();
        try {
            console.log('🔍 [STORY] Fetching stories with filters:', filters);
            const result = await pikadoStoryService.getStories(pagination, filters);
            console.log('✅ [STORY] Stories fetched successfully');
            return result;
        } catch (error) {
            console.error('❌ [STORY] Error fetching stories:', error);
            throw error;
        }
    }

    async publishStory(id: string): Promise<Story> {
        ensureInit();
        try {
            console.log('🔍 [STORY] Publishing story with id:', id);
            const result = await pikadoStoryService.publishStory(id);
            console.log('✅ [STORY] Story published successfully:', result);
            return result;
        } catch (error) {
            console.error('❌ [STORY] Error publishing story:', error);
            throw error;
        }
    }

    async unpublishStory(id: string): Promise<Story> {
        ensureInit();
        try {
            console.log('🔍 [STORY] Unpublishing story with id:', id);
            const result = await pikadoStoryService.unpublishStory(id);
            console.log('✅ [STORY] Story unpublished successfully:', result);
            return result;
        } catch (error) {
            console.error('❌ [STORY] Error unpublishing story:', error);
            throw error;
        }
    }

    async addChapterToStory(input: AddChapterToStoryInput): Promise<Chapter> {
        ensureInit();
        try {
            console.log('🔍 [CHAPTER] Adding chapter to story:', input);
            const result = await pikadoChapterService.addChapterToStory(input);
            console.log('✅ [CHAPTER] Chapter added successfully:', result);
            return result;
        } catch (error) {
            console.error('❌ [CHAPTER] Error adding chapter to story:', error);
            throw error;
        }
    }

    async updateChapter(input: UpdateChapterInput): Promise<Chapter> {
        ensureInit();
        try {
            console.log('🔍 [CHAPTER] Updating chapter:', input);
            const result = await pikadoChapterService.updateChapter(input);
            console.log('✅ [CHAPTER] Chapter updated successfully:', result);
            return result;
        } catch (error) {
            console.error('❌ [CHAPTER] Error updating chapter:', error);
            throw error;
        }
    }

    async deleteChapter(id: string): Promise<boolean> {
        ensureInit();
        try {
            console.log('🔍 [CHAPTER] Deleting chapter with id:', id);
            const result = await pikadoChapterService.deleteChapter(id);
            console.log('✅ [CHAPTER] Chapter deleted successfully:', result);
            return result;
        } catch (error) {
            console.error('❌ [CHAPTER] Error deleting chapter:', error);
            throw error;
        }
    }
}


